import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faAngleDown, faAngleUp } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { sumBy } from 'lodash'
import { useHistory, useParams } from 'react-router-dom'
import Big from 'big.js'

import { useLazyListCartsQuery } from 'orders/reduxQueries'
import { Cart as CartSchema } from 'orders/schema'
import { LoadDataParams, ReactTable, SortProps, TableColumn } from 'uiComponents/table/reactTable'
import { BaseRouteParams, useNavigation } from 'hocs'
import Money from 'uiComponents/money'
import { OnClickMenu, OnClickMenuItem } from 'uiComponents/menus'
import { pathUtils } from 'utils/pathnameFormatter'
import PaymentDeadlineCell from './paymentDeadlineCell'
import { UnstyledLink } from 'uiComponents/navigation/unstyledLink'
import { ORDER_PATHS } from 'orders/paths'
import { MarkCartAsPaidConfirmationDialog } from './modals/markCartAsPaidConfirmationDialog'
import TooltipWrapper from 'uiComponents/tooltip/tooltipWrapper'
import './cartTable.scss'
import { dateRangeFromQuery } from 'dateRanges'
import { format } from 'date-fns'
import { InlineEdit } from 'uiComponents/input'
import { DateFormats, formatISOString } from 'utils'
import CartTableCell from './cartTableCell'
import { Sorting } from 'uiComponents/table'
import { camelToSnakeCase } from 'utils/camelToSnake'
import CartTableFilters from './filters/cartTableFilter'
import { getFiltersFromUrl } from 'uiComponents/filter/filterHelpers'
import StatusLabel from 'orders/orders/commons/orderStatusLabel'
import { useHasFeature } from 'features'
import ConfirmCancelCartDialog from './modals/confirmCancelCartDialog'
import { Download } from 'lucide-react'
import { PageActionButton } from 'uiComponents/buttons/pageAddButton'
import { TableToolbar } from 'uiComponents/table/tableToolbar'
import SearchField from 'uiComponents/search/searchField'
import { SearchScopeBadge } from 'uiComponents/searchScopeBadge'
import CartFilters from './filters/searchAndDate'
import { useTranslation } from 'translations'

const emptyData = [] as any[]
const defaultPageSize = 10

interface CartTableProps {
    onSortChange: (sort: SortProps | null) => void
    onExport?: () => void
}

const CartTable = ({ onSortChange, onExport }: CartTableProps) => {
    const { t } = useTranslation()
    const [listCarts, { isFetching, data }] = useLazyListCartsQuery()
    const { accountSlug } = useParams<BaseRouteParams>()
    const navigation = useNavigation()
    const history = useHistory()
    const query = navigation.query()
    const [markAsPaidCartId, setMarkAsPaidCartId] = useState<string | null>(null)
    const initialSort = useMemo(() => ({ prop: '', direction: 'desc' } as Sorting), [])
    const [cartCancellationId, setCartCancellationId] = useState<string | null>(null)
    const [paginationState, setPaginationState] = useState<LoadDataParams | null>(null)
    const canSeeSentEmails = useHasFeature('show_sent_emails')
    const areFiltersEnabled = useHasFeature('show_reservation_filters')
    const canCancelReservations = useHasFeature('can_cancel_reservations')
    const shouldShowOpenStatus = useHasFeature('should_show_open_status')
    const defaultStatusFilter = shouldShowOpenStatus ? 'open,pending,initiated' : 'pending,initiated'

    useEffect(() => {
        const statusFilter = getFiltersFromUrl('reservation_status')
        if (statusFilter !== '') return

        const searchParams = new URLSearchParams(location.search)
        searchParams.set('filter', `reservation_status:${defaultStatusFilter}`)

        history.replace({
            pathname: location.pathname,
            search: searchParams.toString(),
        })
    }, [navigation])

    const dateRangeParams = useMemo(() => {
        const { dateRangeType } = query
        const { from, to } = dateRangeFromQuery(query, 'last30days')
        const dateFilterKey = dateRangeType || 'created_at'

        return {
            date_from: format(from, 'yyyy-MM-dd'),
            date_to: format(to, 'yyyy-MM-dd'),
            filter_by_date: dateFilterKey,
        }
    }, [navigation])

    const statusFilter = useMemo(() => getFiltersFromUrl('reservation_status'), [navigation])
    const locationFilter = useMemo(() => getFiltersFromUrl('location_id'), [navigation])

    const onLoadData = useCallback(
        ({ token, pagination = { pageSize: defaultPageSize }, sort, ...rest }: LoadDataParams) => {
            const orderKey = camelToSnakeCase(sort.prop)
            const ordering = !orderKey ? undefined : sort.direction === 'asc' ? orderKey : `-${orderKey}`

            listCarts({
                accountSlug,
                query: pathUtils.formatSearchParams({
                    cursor: token,
                    page_size: pagination.pageSize || defaultPageSize,
                    search: query.q,
                    ordering,
                    status: statusFilter,
                    locations: locationFilter,
                    ...dateRangeParams,
                }),
            })
            onSortChange(sort)
            setPaginationState({ token, pagination, sort, ...rest })
        },
        [accountSlug, dateRangeParams, statusFilter, onSortChange],
    )

    const refetchData = useCallback(() => {
        if (!paginationState) return
        onLoadData(paginationState)
    }, [paginationState, onLoadData])

    const openSentEmails = useCallback(
        (id: string) => {
            history.push(`/account/${accountSlug}/orders/pending-reservations/${id}/sent_emails`)
        },
        [history, accountSlug],
    )

    const columns = useMemo(
        () =>
            [
                {
                    Header: () => null,
                    id: 'expander',
                    accessor: 'expander',
                    width: '3.5rem',
                    disableSortBy: true,
                    disableHideColumn: true,
                    Cell: ({ row }) => {
                        return (
                            <div {...row.getToggleRowExpandedProps()}>
                                {row.isExpanded ? (
                                    <FontAwesomeIcon icon={faAngleUp as IconProp} className="opened-icon" />
                                ) : (
                                    <FontAwesomeIcon icon={faAngleDown as IconProp} className="closed-icon" />
                                )}
                            </div>
                        )
                    },
                },
                {
                    accessor: 'location.name',
                    Header: t('orders.cart.columns.location'),
                    width: '7.3rem',
                    disableSortBy: true,
                    Cell: ({ value }) => <div title={value}>{value}</div>,
                },
                {
                    accessor: 'status',
                    Header: t('orders.cart.columns.status'),
                    width: '6rem',
                    Cell: ({ value }) => <StatusLabel status={value} />,
                },
                {
                    accessor: 'referenceId',
                    Header: t('orders.cart.columns.reservationId'),
                    width: '10rem',
                    disableSortBy: true,
                    Cell: ({ value }) => <span title={value}>{value}</span>,
                },
                {
                    accessor: 'customer.email',
                    Header: t('orders.cart.columns.email'),
                    width: '15.5rem',
                    disableSortBy: true,
                    Cell: ({ value }) => (
                        <InlineEdit id="email" value={value} maskData={true} maskIcon={true} disabled>
                            {value}
                        </InlineEdit>
                    ),
                },
                {
                    accessor: 'quantity',
                    Header: t('orders.cart.columns.qty'),
                    width: '4rem',
                    disableSortBy: true,
                    Cell: ({ row: { original } }) => {
                        return sumBy(
                            original.items.filter((item) => !!item.amount),
                            (cartItem) => cartItem.amount,
                        )
                    },
                },
                {
                    accessor: 'total',
                    Header: t('orders.cart.columns.total'),
                    width: '6rem',
                    disableSortBy: true,
                    Cell: ({ row: { original } }) => {
                        const total = Big(original.totalPriceAfterDiscount).toNumber()
                        return <Money amount={total} accountSlug={accountSlug} />
                    },
                },
                {
                    accessor: 'expiresAt',
                    Header: t('orders.cart.columns.paymentDeadline'),
                    width: '10.5rem',
                    Cell: ({
                        value,
                        row: {
                            original: { id, timezone },
                        },
                    }) => <PaymentDeadlineCell value={value} cartId={id} timezone={timezone} />,
                },
                {
                    accessor: 'eventDate',
                    Header: t('orders.cart.columns.visitDate'),
                    width: '8rem',
                    Cell: ({ value }) => (value ? formatISOString(value, `${DateFormats.LONG_DATE}`) : '-'),
                },
                {
                    accessor: 'startTime',
                    Header: t('orders.cart.columns.visitTime'),
                    width: '6.25rem',
                    disableSortBy: true,
                    Cell: ({ value }) => {
                        if (!value) return '-'
                        const [hours, minutes, _seconds] = value.split(':')
                        const formattedTime = `${hours}:${minutes}`
                        return <span title={formattedTime}>{formattedTime}</span>
                    },
                },
                {
                    accessor: 'createdAt',
                    Header: t('orders.cart.columns.createdOn'),
                    width: '9rem',
                    Cell: ({ value, row: { original } }) =>
                        value
                            ? formatISOString(value, `${DateFormats.LONG_DATE}, ${DateFormats.SHORT_TIME}`, {
                                  timeZone: original.timezone,
                              })
                            : '-',
                },
                {
                    accessor: 'updatedAt',
                    Header: t('orders.cart.columns.lastEdit'),
                    width: '9rem',
                    disableSortBy: true,
                    Cell: ({ value, row: { original } }) =>
                        value
                            ? formatISOString(value, `${DateFormats.LONG_DATE}, ${DateFormats.SHORT_TIME}`, {
                                  timeZone: original.timezone,
                              })
                            : '-',
                },
                {
                    accessor: 'id',
                    align: 'right',
                    style: {
                        overflow: 'visible',
                    },
                    width: '9.5rem',
                    classNames: '',
                    disableSortBy: true,
                    Cell: ({ value, cell }) => {
                        const { status, urls = [] } = cell.row.original
                        const isReservationViewOnly = ['completed', 'cancelled'].includes(status)
                        const reservationViewOnlyTooltipText = t('orders.cart.actions.viewOnlyTooltip', { status })

                        return (
                            <TooltipWrapper
                                hidden={!isReservationViewOnly}
                                text={reservationViewOnlyTooltipText}
                                tooltipStyle={{ zIndex: 1000 }}
                                delayShow={300}
                            >
                                <OnClickMenu
                                    title={t('orders.cart.actions.title')}
                                    kind="action"
                                    secondary
                                    usePortal
                                    disabled={isReservationViewOnly}
                                >
                                    <OnClickMenuItem>
                                        {!urls.find(({ rel }) => rel === 'resume')?.url ? (
                                            <TooltipWrapper
                                                text={t('orders.cart.actions.paymentInitiated')}
                                                className="disabled-dropdown-item"
                                            >
                                                {t('orders.cart.actions.editReservation')}
                                            </TooltipWrapper>
                                        ) : (
                                            <UnstyledLink
                                                to={pathUtils.populateParams(ORDER_PATHS.fullPaths.cartEditPage, {
                                                    accountSlug,
                                                    id: value,
                                                })}
                                            >
                                                {t('orders.cart.actions.editCart')}
                                            </UnstyledLink>
                                        )}
                                    </OnClickMenuItem>
                                    <OnClickMenuItem onClick={() => setMarkAsPaidCartId(value)}>
                                        {t('orders.cart.actions.markAsPaid')}
                                    </OnClickMenuItem>
                                    {canCancelReservations &&
                                        !['completed', 'cancelled'].includes(cell.row.original.status) && (
                                            <OnClickMenuItem onClick={() => setCartCancellationId(value)}>
                                                {t('orders.cart.actions.cancelReservation')}
                                            </OnClickMenuItem>
                                        )}
                                    {canSeeSentEmails && (
                                        <OnClickMenuItem onClick={() => openSentEmails(value)}>
                                            {t('orders.cart.actions.sentEmails')}
                                        </OnClickMenuItem>
                                    )}
                                </OnClickMenu>
                            </TooltipWrapper>
                        )
                    },
                },
            ] as TableColumn<CartSchema>[],
        [accountSlug],
    )

    const hideMarkAsPaidDialog = useCallback(() => {
        setMarkAsPaidCartId(null)
    }, [setMarkAsPaidCartId])

    const hideCancelCartDialog = useCallback(
        (type: 'close' | 'confirm') => () => {
            setCartCancellationId(null)

            if (type === 'confirm') {
                refetchData()
            }
        },
        [refetchData, setCartCancellationId],
    )

    return (
        <div className="cart-table-container">
            <TableToolbar>
                <div className="cart-table-search">
                    <SearchField placeholder={t('orders.cart.searchPlaceholder')} className="cart-search" />
                    <SearchScopeBadge />
                    <CartFilters />
                </div>
                <div className="header-group">
                    {areFiltersEnabled && <CartTableFilters />}
                    {onExport && (
                        <PageActionButton icon={Download} title={t('orders.cart.exportTitle')} onClick={onExport}>
                            {t('orders.cart.exportButton')}
                        </PageActionButton>
                    )}
                </div>
            </TableToolbar>
            <ReactTable
                sticky
                noResultsRow
                pagination={{
                    next: data?.next,
                    previous: data?.previous,
                }}
                sort={initialSort}
                expanded={CartTableCell}
                loading={isFetching}
                loadData={onLoadData}
                data={data?.results || emptyData}
                columns={columns}
                storeStateInQuery={false}
                elevation={true}
                tableProps={{ className: 'cart-table' }}
            />
            <MarkCartAsPaidConfirmationDialog
                accountSlug={accountSlug}
                cartId={markAsPaidCartId}
                onConfirm={hideMarkAsPaidDialog}
                onCancel={hideMarkAsPaidDialog}
            />
            <ConfirmCancelCartDialog
                cartId={cartCancellationId}
                onClose={hideCancelCartDialog('close')}
                onConfirm={hideCancelCartDialog('confirm')}
            />
        </div>
    )
}

export default CartTable
